/**
 * Data Comparison Logger
 * Logs backend vs frontend data comparison directly to browser console
 *
 * ENABLED BY DEFAULT - Set to false to disable
 */

const LOGGING_ENABLED = true; // Set to false to disable comparison logging

/**
 * Logs comparison between backend and frontend data directly to console
 * @param {string} workflowId - The workflow ID
 * @param {object} backendData - Data from backend/workflow response
 * @param {object} frontendData - Data from frontend v2Data state
 */
export async function logDataComparison(workflowId, backendData, frontendData) {
  if (!LOGGING_ENABLED) {
    return;
  }

  const comparison = {
    workflowId,
    timestamp: new Date().toISOString(),

    backendData: {
      research: backendData?.research,
      validation: backendData?.validation,
      analysis: backendData?.analysis,
      prompts: backendData?.prompts,
      flowDiagram: backendData?.flowDiagram || backendData?.diagrams
    },

    frontendData: {
      research: frontendData?.research,
      validation: frontendData?.validation,
      analysis: frontendData?.analysis,
      prompts: frontendData?.prompts,
      flowDiagram: frontendData?.flowDiagram
    },

    differences: detectDifferences(backendData, frontendData),

    // Deep inspection of critical analysis fields
    criticalFieldsInspection: inspectCriticalFields(backendData, frontendData),

    metadata: {
      platform: frontendData?.platform,
      originalIdea: frontendData?.originalIdea
    }
  };

  // Output to console with clear formatting
  logCriticalFieldsReport(comparison.criticalFieldsInspection);
}

/**
 * Inspects critical nested fields in analysis data
 * @param {object} backend - Backend data
 * @param {object} frontend - Frontend data
 * @returns {object} Inspection results
 */
function inspectCriticalFields(backend, frontend) {
  const backendAnalysis = backend?.analysis;
  const frontendAnalysis = frontend?.analysis;

  const backendBlueprint = backendAnalysis?.cursorBlueprint;
  const frontendBlueprint = frontendAnalysis?.cursorBlueprint;

  const inspection = {
    analysisExists: {
      backend: !!backendAnalysis,
      frontend: !!frontendAnalysis
    },
    blueprintExists: {
      backend: !!backendBlueprint,
      frontend: !!frontendBlueprint
    },
    fields: {}
  };

  // Critical fields to inspect (4-layer + cross-cutting structure)
  const criticalPaths = [
    // 4 Core Layers
    { path: 'presentationLayer', name: 'Presentation Layer' },
    { path: 'presentationLayer.stateManagement', name: 'State Management' },
    { path: 'businessLayer', name: 'Business Layer' },
    { path: 'persistenceLayer.dataAccess', name: 'Data Access Patterns' },
    { path: 'databaseLayer.schema', name: 'Database Schema' },
    // Cross-cutting Concerns
    { path: 'crossCuttingConcerns.security.authorization.rbac', name: 'RBAC' },
    { path: 'crossCuttingConcerns.security.authorization', name: 'Authorization' },
    { path: 'crossCuttingConcerns.deployment', name: 'Deployment' },
    { path: 'crossCuttingConcerns.testing', name: 'Testing' },
    // Metadata
    { path: 'solutionOverview', name: 'Solution Overview' }
  ];

  criticalPaths.forEach(({ path, name }) => {
    const backendValue = getNestedValue(backendBlueprint, path);
    const frontendValue = getNestedValue(frontendBlueprint, path);

    inspection.fields[name] = {
      path,
      backend: {
        exists: backendValue !== undefined,
        value: backendValue,
        type: typeof backendValue,
        isEmpty: isEmptyValue(backendValue),
        summary: summarizeValue(backendValue)
      },
      frontend: {
        exists: frontendValue !== undefined,
        value: frontendValue,
        type: typeof frontendValue,
        isEmpty: isEmptyValue(frontendValue),
        summary: summarizeValue(frontendValue)
      },
      match: JSON.stringify(backendValue) === JSON.stringify(frontendValue),
      issue: detectIssue(backendValue, frontendValue)
    };
  });

  return inspection;
}

/**
 * Gets nested value from object using dot notation path
 */
function getNestedValue(obj, path) {
  if (!obj) return undefined;
  return path.split('.').reduce((current, key) => current?.[key], obj);
}

/**
 * Checks if value is empty (null, empty array, empty object, etc.)
 */
function isEmptyValue(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object') {
    const keys = Object.keys(value);
    if (keys.length === 0) return true;
    // Check if all values are null/undefined
    return keys.every(k => value[k] === null || value[k] === undefined);
  }
  return false;
}

/**
 * Creates a summary string of the value
 */
function summarizeValue(value) {
  if (value === null || value === undefined) return 'NULL/UNDEFINED';
  if (typeof value === 'string') {
    return value.length > 50 ? `"${value.substring(0, 50)}..."` : `"${value}"`;
  }
  if (Array.isArray(value)) {
    return `Array(${value.length}) [${value.slice(0, 3).join(', ')}${value.length > 3 ? '...' : ''}]`;
  }
  if (typeof value === 'object') {
    const keys = Object.keys(value);
    const nullCount = keys.filter(k => value[k] === null || value[k] === undefined).length;
    return `Object {${keys.slice(0, 3).join(', ')}${keys.length > 3 ? '...' : ''}} (${nullCount}/${keys.length} null)`;
  }
  return String(value);
}

/**
 * Detects issue type with the value
 */
function detectIssue(backendValue, frontendValue) {
  const backendEmpty = isEmptyValue(backendValue);
  const frontendEmpty = isEmptyValue(frontendValue);

  if (!backendValue && !frontendValue) return 'BOTH_MISSING';
  if (!backendValue) return 'MISSING_IN_BACKEND';
  if (!frontendValue) return 'MISSING_IN_FRONTEND';
  if (backendEmpty && frontendEmpty) return 'BOTH_EMPTY';
  if (backendEmpty) return 'BACKEND_EMPTY';
  if (frontendEmpty) return 'FRONTEND_EMPTY';
  if (JSON.stringify(backendValue) !== JSON.stringify(frontendValue)) return 'VALUES_DIFFER';
  return 'OK';
}

/**
 * Logs critical fields report in readable format
 */
function logCriticalFieldsReport(inspection) {
  Object.entries(inspection.fields).forEach(([name, data]) => {
    const icon = data.issue === 'OK' ? '✅' : data.issue.includes('EMPTY') || data.issue.includes('MISSING') ? '❌' : '⚠️';
  });
}

/**
 * Detects differences between backend and frontend data structures
 * @param {object} backend - Backend data
 * @param {object} frontend - Frontend data
 * @returns {object} Comparison results
 */
function detectDifferences(backend, frontend) {
  const differences = [];

  const fields = ['research', 'validation', 'analysis', 'prompts', 'flowDiagram'];

  fields.forEach(field => {
    const backendValue = backend?.[field] || backend?.data?.[field];
    const frontendValue = frontend?.[field];

    const hasBackend = !!backendValue;
    const hasFrontend = !!frontendValue;

    // Check if field exists in both
    if (hasBackend !== hasFrontend) {
      differences.push({
        field,
        status: hasFrontend ? 'missing_in_backend' : 'missing_in_frontend',
        backendKeys: hasBackend ? Object.keys(backendValue || {}) : [],
        frontendKeys: hasFrontend ? Object.keys(frontendValue || {}) : []
      });
    } else if (hasBackend && hasFrontend) {
      // Both exist, check structure
      const backendKeys = Object.keys(backendValue || {}).sort();
      const frontendKeys = Object.keys(frontendValue || {}).sort();

      if (JSON.stringify(backendKeys) !== JSON.stringify(frontendKeys)) {
        const missingInFrontend = backendKeys.filter(k => !frontendKeys.includes(k));
        const missingInBackend = frontendKeys.filter(k => !backendKeys.includes(k));

        differences.push({
          field,
          status: 'structure_mismatch',
          backendKeys,
          frontendKeys,
          missingInFrontend,
          missingInBackend
        });
      }
    }
  });

  return {
    hasDifferences: differences.length > 0,
    differenceCount: differences.length,
    differences,
    summary: differences.length === 0
      ? 'All fields match between backend and frontend'
      : `Found ${differences.length} difference(s)`
  };
}
